import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTherapists, clearTherapistDetail } from "../../redux/therapistSlice";
import { getLanguages } from "../../redux/languageSlice";
import DOMPurify from "dompurify";

const toTitleCase = (str) =>
  str ? str.trim().split(/\s+/).map(word =>
    word.charAt(0).toLocaleUpperCase("tr") + word.slice(1).toLowerCase()
  ).join(" ") : "";

const OurExpertStaff = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux'tan terapist verilerini al
  const therapistsState = useSelector((state) => state.therapist);
  const therapists = therapistsState?.therapists || [];
  const loading = therapistsState?.loading || false;
  const error = therapistsState?.error || null;

  const languageState = useSelector((state) => state.languages);
  const fetchedLanguages = languageState?.fetchedLanguages || [];

  // Bileşen monte edildiğinde terapistleri getir
  useEffect(() => {
    dispatch(getTherapists());
    dispatch(getLanguages());
  }, [dispatch]);

  // Tüm terapistler sayfasına yönlendirme
  const handleClick = () => {
    navigate("/experts");
  };

  // Sadece onaylı terapistleri dahil et
  const approvedTherapists = therapists.filter(
    (therapist) =>
      therapist.isActive !== false &&
      (therapist.approvalStatus === "approved" ||
      therapist.status === "approved" ||
      therapist.isApprovedByAdmin === true ||
      therapist.isApprovedByAdmin === "true" ||
      therapist.isVerified === true ||
      therapist.isVerified === "true")
  );

  // Sadece ilk 3 onaylı terapisti göster
  const displayedTherapists = approvedTherapists.slice(0, 3);

  // HTML içeriğini güvenli bir şekilde render etmek için ve kısaltma
  const createMarkup = (html, limit = 150) => {
    if (!html) return { __html: "" };

    // HTML etiketlerini temizle ve düz metne dönüştür
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = DOMPurify.sanitize(html);
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    // Metni kısalt
    if (textContent.length > limit) {
      return {
        __html: DOMPurify.sanitize(textContent.substring(0, limit) + "..."),
      };
    }

    return { __html: DOMPurify.sanitize(html) };
  };

  // Eğitim bilgisini formatlama
  const formatEducation = (education) => {
    if (!education || education.length === 0) return "Eğitim bilgisi yok";
    return education[0].school;
  };

  // Yorum sayısını ve puanını formatlama
  const formatReviews = (reviews) => {
    if (!reviews || reviews.length === 0) return { count: 0, rating: 5 };

    // Gerçek yorum sayısı
    const count = reviews.length;

    // Ortalama puanı hesapla (varsayılan olarak 5 üzerinden)
    // Gerçek uygulamada reviews içinde rating değeri olacaktır
    const rating = 5;

    return { count, rating };
  };

  return (
    <div id="our-expert-staff" className="p-5 pt-12 text-center scroll-mt-[200px]">
      <div className="w-[368px] h-[35px] mx-auto mb-5">
        <h2 className="text-[32px] font-roboto text-text mb-5">
          {t("ourExpertStaff")}
        </h2>
      </div>

      {loading ? (
        <div className="text-center mt-10">Terapistler yükleniyor...</div>
      ) : error ? (
        <div className="text-center mt-10 text-red-500">Hata: {error}</div>
      ) : (
        <div className="flex gap-5 justify-center flex-wrap">
          {displayedTherapists.map((therapist) => {
            // Yorum bilgilerini al
            formatReviews(therapist.reviews);

            return (
              <div
                key={`therapist-${therapist._id}`}
                className="relative overflow-hidden border-2 rounded-[45px] shadow-md p-4 bg-tertiary w-full sm:w-[370px] min-h-[420px] flex flex-col justify-between"
              >
                {/* Üst Alan */}
                <div className="flex items-center">
                  <div className="w-[99px] h-[99px]">
                    {therapist.picture ? (
                      <img
                        src={therapist.picture}
                        alt={`${therapist.name} ${therapist.surname}`}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xl">
                        {therapist.name.charAt(0)}
                        {therapist.surname.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="ml-4 text-center">
                    <h3 className="text-[20px] font-poppins italic text-text">
                      {toTitleCase(therapist.name)} {therapist.surname}
                    </h3>
                    <p className="text-[16px] text-gray-600 font-poppins italic ml-2">
                      {therapist.branchId?.name || "Therapist"}
                    </p>
                    <hr className="my-2 border-gray-300" />
                    <div className="flex items-center justify-center gap-1">
                      {/* Yıldızlar - Gerçek rating'e göre dolu veya boş yıldız göster */}
                      {Array(5)
                        .fill(0)
                        .map((_, i) => (
                          <svg
                            key={`star-${therapist._id}-${i}`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            stroke="none"
                            viewBox="0 0 24 24"
                            className="w-4 h-4 text-yellow-400"
                          >
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                          </svg>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Orta Alan - Bio veya Eğitim Bilgisi - Scroll yerine kısaltılmış metin */}
                <div className="mt-8 w-full min-h-[110px] overflow-hidden">
                  {therapist.bio ? (
                    <div
                      className="font-poppins italic text-[16px] text-left break-words"
                      dangerouslySetInnerHTML={createMarkup(therapist.bio, 150)}
                    />
                  ) : (
                    <p className="font-poppins italic text-[16px] text-left break-words">
                      {formatEducation(therapist.education)}
                    </p>
                  )}
                </div>

                {/* Alt Alan - Sertifikalar ve Buton */}
                <div className="flex justify-between mt-20">
                  <div className="flex flex-col">
                    <span className="text-[12px] font-poppins font-bold text-primary px-1 uppercase mb-1">
                      {t("languages")}
                    </span>
                    <hr className="border-text border-[2px] rounded mb-1" />
                    {therapist.languages && therapist.languages.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {[...therapist.languages]
                          .sort((a, b) => {
                            const getCodeLabel = (lang) => {
                              const langId = typeof lang === 'object' ? lang._id : lang;
                              const found = fetchedLanguages.find(l => l._id === langId);
                              const code = (found?.code || (typeof lang === 'object' ? lang.code : '') || '').toUpperCase();
                              const name = (found?.name || (typeof lang === 'object' ? lang.name : '') || '').toUpperCase();
                              return { code, name };
                            };
                            const labelA = getCodeLabel(a);
                            const labelB = getCodeLabel(b);
                            const isEnA = labelA.code === 'EN' || labelA.name === 'ENGLISH';
                            const isEnB = labelB.code === 'EN' || labelB.name === 'ENGLISH';
                            if (isEnA && !isEnB) return -1;
                            if (!isEnA && isEnB) return 1;
                            return 0;
                          })
                          .map((langData, idx) => {
                            const langId = typeof langData === 'object' ? langData._id : langData;
                            const foundLang = fetchedLanguages.find(l => l._id === langId);
                            const displayLabel = foundLang 
                              ? (foundLang.code || foundLang.name) 
                              : (typeof langData === 'object' ? (langData.code || langData.name) : '...');
                            
                            return (
                              <span 
                                key={idx} 
                                className="text-[10px] font-poppins font-bold text-primary bg-tertiary px-1 rounded uppercase"
                              >
                                {displayLabel}
                              </span>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      dispatch(clearTherapistDetail());
                      navigate(`/therapistInfo/${therapist._id}`);
                    }}
                    className="py-2 px-4 w-[150px] h-[43px] rounded-full font-roboto text-[16px] text-white transition-transform transform hover:scale-105 active:scale-95 duration-300 ease-in-out bg-button-bg"
                  >
                    {t("detailTherapist")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        className="lg:w-[746px] sm:w-[375px] h-[55px] mt-5 bg-button-bg text-white py-2 font-roboto text-[24px] leading-[26px] px-5 rounded-full hover:bg-tertiary hover:text-primary hover:rounded-full transition-transform transform hover:scale-105 active:scale-95 duration-300 ease-in-out"
        onClick={handleClick}
      >
        {t("seeAllPsychologists")}
      </button>
    </div>
  );
};

export default OurExpertStaff;
